'use client'

import { type CSSProperties, useEffect, useRef, useState } from 'react'

import { cn } from '../ui/cn'

export interface AutoScrollerImage {
  imgUrl: string
  imgAlt: string
  /** Per-logo overrides, for artwork that needs a different height. */
  imgClassName?: string
}

interface AutoScrollerProps {
  images: readonly AutoScrollerImage[]
  duration?: number
  className?: string
  imageClassName?: string
}

interface TrackStyle extends CSSProperties {
  '--snplay-auto-scroll-distance': string
}

export const AutoScroller = ({
  images,
  duration = 30,
  className,
  imageClassName,
}: AutoScrollerProps): React.ReactElement | null => {
  const containerRef = useRef<HTMLDivElement>(null)
  const sequenceRef = useRef<HTMLUListElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [{ copyCount, sequenceWidth }, setLoopMeasurements] = useState({
    copyCount: 2,
    sequenceWidth: 0,
  })

  useEffect(() => {
    const container = containerRef.current

    if (!container) return

    if (!('IntersectionObserver' in window)) {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting)
    })

    observer.observe(container)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const container = containerRef.current
    const sequence = sequenceRef.current

    if (!container || !sequence) return

    const measureLoop = (): void => {
      const nextSequenceWidth = sequence.getBoundingClientRect().width

      if (nextSequenceWidth === 0) return

      const nextCopyCount = Math.max(
        2,
        Math.ceil(
          (container.getBoundingClientRect().width + nextSequenceWidth) / nextSequenceWidth,
        ),
      )

      setLoopMeasurements((current) => {
        if (current.copyCount === nextCopyCount && current.sequenceWidth === nextSequenceWidth) {
          return current
        }

        return {
          copyCount: nextCopyCount,
          sequenceWidth: nextSequenceWidth,
        }
      })
    }

    measureLoop()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureLoop)
      return () => window.removeEventListener('resize', measureLoop)
    }

    const resizeObserver = new ResizeObserver(measureLoop)
    resizeObserver.observe(container)
    resizeObserver.observe(sequence)

    return () => resizeObserver.disconnect()
  }, [images])

  if (images.length === 0) return null

  const renderImages = (isDuplicate = false): React.ReactElement[] =>
    images.map(({ imgUrl, imgAlt, imgClassName }, index) => (
      <li
        className="snplay:flex snplay:shrink-0 snplay:items-center snplay:justify-center"
        key={`${imgUrl}-${index}`}
      >
        <img
          className={cn(
            'snplay:h-20 snplay:w-auto snplay:max-w-48 snplay:object-contain',
            imageClassName,
            imgClassName,
          )}
          alt={isDuplicate ? '' : imgAlt}
          aria-hidden={isDuplicate || undefined}
          loading="lazy"
          decoding="async"
          src={imgUrl}
        />
      </li>
    ))

  const trackStyle: TrackStyle = {
    '--snplay-auto-scroll-distance': `-${sequenceWidth}px`,
    animationDuration: `${duration}s`,
    animationPlayState: isVisible && sequenceWidth > 0 ? 'running' : 'paused',
  }

  return (
    <div
      ref={containerRef}
      className={cn('snplay-auto-scroller snplay:overflow-hidden', className)}
      aria-label="Scrolling image carousel"
      role="region"
    >
      <div className="snplay-auto-scroller-track snplay:flex snplay:w-max" style={trackStyle}>
        {Array.from({ length: copyCount }, (_, copyIndex) => (
          <ul
            ref={copyIndex === 0 ? sequenceRef : undefined}
            className={cn(
              'snplay:flex snplay:shrink-0 snplay:items-center',
              'snplay:gap-20 snplay:pr-20 snplay:md:gap-30 snplay:md:pr-30',
            )}
            aria-hidden={copyIndex > 0 ? 'true' : undefined}
            key={copyIndex}
          >
            {renderImages(copyIndex > 0)}
          </ul>
        ))}
      </div>
    </div>
  )
}
